import React, { useState, useEffect } from "react";

import {
  Autocomplete,
  TextField,
  TextFieldProps,
  CircularProgress,
  AutocompleteProps,
  TextFieldVariants,
} from "@mui/material";

import useDebounce from "hooks/useDebounce";
import { PaginatedResult } from "types/common-types";

export type AsyncAutocompleteWithQueryProps<T> = {
  value: T | null;
  onChange: (v: T | null) => void;
  onInputChange?: (value: string) => void;
  fetchData: (
    searchTerm: string
  ) => Promise<{ result: PaginatedResult<T>; forSearchTerm: string }>;
  failMessage: string;
  className?: string;
  label?: string;
  variant?: TextFieldVariants;
  required?: boolean;
  error?: boolean;
  textFieldProps?: Partial<TextFieldProps>;
  labelField: keyof T;
  idField: keyof T;
  isLoadingInitialValue?: boolean;
  TextFieldComponent?: typeof TextField;
} & Partial<
  Omit<
    AutocompleteProps<T, false, false, false>,
    "onInputChange" | "onChange" | "value" | "multiple"
  >
>;

const AsyncAutocompleteWithQuery = <T extends {}>({
  fetchData,
  failMessage,
  className,
  label,
  onChange,
  onInputChange,
  value,
  variant = "standard",
  required,
  textFieldProps,
  labelField,
  idField,
  isLoadingInitialValue,
  renderInput,
  TextFieldComponent = TextField,
  ...rest
}: AsyncAutocompleteWithQueryProps<T>) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const trimmedSearchTerm = searchTerm.trim();
  const debouncedSearchTerm = useDebounce(trimmedSearchTerm, 500);
  const [options, setOptions] = useState<T[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    let active = true;

    setIsFetching(true);
    fetchData(debouncedSearchTerm)
      .then(({ result, forSearchTerm }) => {
        if (active) {
          let newOptions = [] as T[];

          if (result.data) {
            newOptions = result.data.filter(
              (v) => v[idField] !== value?.[idField]
            );
          }

          if (forSearchTerm === debouncedSearchTerm) setIsFetching(false);
          setOptions(newOptions);
        }
      })
      .catch(() => {
        setOptions(value ? [value] : []);
      });

    return () => {
      active = false;
    };
  }, [value, fetchData, debouncedSearchTerm, idField, isLoadingInitialValue]);

  const handleValueChange = (e: React.ChangeEvent<{}>, v: T | null) => {
    if (onChange) onChange(v);
  };

  const optionsWithValue = value ? [value, ...options] : options;

  return (
    <Autocomplete
      className={className}
      getOptionLabel={(option) =>
        ((option[labelField] as unknown) as string) || ""
      }
      isOptionEqualToValue={(option: any) =>
        (option[idField] as unknown) === value?.[idField]
      }
      options={isLoadingInitialValue ? [] : optionsWithValue}
      autoComplete
      value={value}
      multiple={false}
      onChange={(e, value: string | T | null) => {
        handleValueChange(e, value as T | null);
      }}
      onInputChange={(event, newSearchTerm) => {
        setSearchTerm(newSearchTerm);
        if (onInputChange) onInputChange(newSearchTerm);
      }}
      disabled={isLoadingInitialValue}
      loading={isFetching || isLoadingInitialValue}
      {...rest}
      renderInput={(params) =>
        renderInput !== undefined ? (
          renderInput(params)
        ) : (
          <TextFieldComponent
            variant={variant}
            {...params}
            {...textFieldProps}
            InputProps={{
              ...textFieldProps?.InputProps,
              ...params.InputProps,
              endAdornment: (
                <>
                  {isFetching || isLoadingInitialValue ? (
                    <CircularProgress size={16} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            InputLabelProps={{ required }}
            label={label}
            fullWidth
            data-cy={`${label}DropDown`}
          />
        )
      }
    />
  );
};

export default AsyncAutocompleteWithQuery;
